import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Download, Search } from "lucide-react";

const ALL = "__all__";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function fmtDuration(mins) {
  if (mins == null) return "—";
  const h = Math.floor(mins / 60);
  const m = Math.round(mins - h * 60);
  return `${h}h ${m}m`;
}

export default function Records() {
  const [events, setEvents] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ volunteer: "", event_id: "", start: "", end: "", status: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.volunteer) params.volunteer = filters.volunteer;
      if (filters.event_id) params.event_id = filters.event_id;
      if (filters.start) params.start = new Date(filters.start).toISOString();
      if (filters.end) {
        const d = new Date(filters.end);
        d.setHours(23, 59, 59, 999);
        params.end = d.toISOString();
      }
      if (filters.status) params.status = filters.status;
      const { data } = await api.get("/records", { params });
      setRecords(data);
    } catch { toast.error("Could not load records."); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/events");
        setEvents(data);
      } catch { /* ignore */ }
    })();
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totalMinutes = useMemo(
    () => records.reduce((a, r) => a + (r.duration_minutes || 0), 0),
    [records]
  );

  const exportCsv = async () => {
    const params = new URLSearchParams();
    if (filters.volunteer) params.set("volunteer", filters.volunteer);
    if (filters.event_id) params.set("event_id", filters.event_id);
    if (filters.start) params.set("start", new Date(filters.start).toISOString());
    if (filters.end) {
      const d = new Date(filters.end);
      d.setHours(23, 59, 59, 999);
      params.set("end", d.toISOString());
    }
    const url = `${API}/records/export?${params.toString()}`;
    // credentials via cookies
    try {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "volunteer_records.csv";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Could not export CSV."); }
  };

  return (
    <div className="max-w-6xl" data-testid="records-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="overline">Data</p>
          <h1 className="font-heading text-4xl font-light tracking-tight mt-2">Records</h1>
          <p className="text-brand-muted font-body mt-2">Every clock-in and clock-out.</p>
        </div>
        <button
          onClick={exportCsv} data-testid="export-csv-button"
          className="h-11 px-5 rounded-md border border-brand-border bg-white font-body font-medium flex items-center gap-2 transition-colors hover:bg-brand-bg"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      <div className="mt-8 bg-white border border-brand-border rounded-md p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <Label className="overline block mb-2">Volunteer</Label>
            <Input
              data-testid="filter-volunteer"
              value={filters.volunteer}
              onChange={(e) => setFilters({ ...filters, volunteer: e.target.value })}
              placeholder="Search name"
              className="bg-white"
            />
          </div>
          <div>
            <Label className="overline block mb-2">Event</Label>
            <Select
              value={filters.event_id || ALL}
              onValueChange={(v) => setFilters({ ...filters, event_id: v === ALL ? "" : v })}
            >
              <SelectTrigger data-testid="filter-event" className="bg-white"><SelectValue placeholder="All events" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value={ALL}>All events</SelectItem>
                {events.map((ev) => (
                  <SelectItem key={ev.id} value={ev.id}>{ev.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline block mb-2">From</Label>
            <Input
              type="date" data-testid="filter-start"
              value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })}
              className="bg-white"
            />
          </div>
          <div>
            <Label className="overline block mb-2">To</Label>
            <Input
              type="date" data-testid="filter-end"
              value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })}
              className="bg-white"
            />
          </div>
          <div>
            <Label className="overline block mb-2">Status</Label>
            <Select
              value={filters.status || ALL}
              onValueChange={(v) => setFilters({ ...filters, status: v === ALL ? "" : v })}
            >
              <SelectTrigger data-testid="filter-status" className="bg-white"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value={ALL}>All</SelectItem>
                <SelectItem value="active">Currently clocked in</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-5 flex items-center gap-3">
          <button
            onClick={load} data-testid="apply-filters-button"
            className="h-10 px-5 rounded-md bg-brand-primary text-white font-body font-medium flex items-center gap-2 transition-colors hover:bg-brand-primaryHover"
          >
            <Search className="w-4 h-4" /> Apply
          </button>
          <button
            onClick={() => { setFilters({ volunteer: "", event_id: "", start: "", end: "", status: "" }); setTimeout(load, 0); }}
            data-testid="clear-filters-button"
            className="h-10 px-4 rounded-md border border-brand-border font-body transition-colors hover:bg-brand-bg"
          >Clear</button>
          <p className="ml-auto text-sm text-brand-muted font-body" data-testid="records-totals">
            {records.length} record{records.length !== 1 ? "s" : ""} · {fmtDuration(totalMinutes)} total
          </p>
        </div>
      </div>

      <div className="mt-8 bg-white border border-brand-border rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-brand-bg">
              <TableHead>Volunteer</TableHead>
              <TableHead>Event</TableHead>
              <TableHead>Clock in</TableHead>
              <TableHead>Clock out</TableHead>
              <TableHead className="text-right">Duration</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={5} className="text-brand-muted">Loading…</TableCell></TableRow>
            )}
            {!loading && records.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-brand-muted py-10 text-center" data-testid="records-empty">
                  No records match these filters yet.
                </TableCell>
              </TableRow>
            )}
            {records.map((r) => (
              <TableRow key={r.id} data-testid={`record-row-${r.id}`}>
                <TableCell className="font-body font-medium">{r.volunteer_name}</TableCell>
                <TableCell>{r.event_name || "—"}</TableCell>
                <TableCell className="text-brand-muted">{fmtDateTime(r.clock_in)}</TableCell>
                <TableCell className="text-brand-muted">
                  {r.clock_out ? fmtDateTime(r.clock_out) : (
                    <span className="text-brand-primary font-medium">Active</span>
                  )}
                </TableCell>
                <TableCell className="text-right font-heading tabular-nums">{fmtDuration(r.duration_minutes)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
